import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import ChangePlanModal from '../modals/ChangePlanModal';
import { IcoCheck, IcoX, IcoLock, IcoSettings, IcoSun, IcoMoon, IcoTrash } from '../dashboard/DashIcons';
import '../../styles/admin-dashboard.css';

const SECTIONS = [
  { id: 'profile',      label: 'Profile',                  roles: ['superadmin', 'admin', 'manager'] },
  { id: 'organization', label: 'Organization & Labels',    roles: ['superadmin', 'admin'] },
  { id: 'system',       label: 'System Preferences',      roles: ['superadmin', 'admin'] },
  { id: 'features',     label: 'Features',                roles: ['superadmin', 'admin'] },
  { id: 'timeoff',      label: 'Time Off Policy',         roles: ['superadmin', 'admin'] },
  { id: 'notifications',label: 'Notifications',           roles: ['superadmin', 'admin', 'manager'] },
  { id: 'integrations',      label: 'Integrations & API',      roles: ['superadmin', 'admin'] },
  { id: 'payrollVisibility', label: 'Payroll Visibility',       roles: ['superadmin'] },
  { id: 'subscription',      label: 'Subscription & Billing',  roles: ['superadmin'] },
];

const inputStyle = {
  width: '100%', padding: '0.65rem 0.875rem',
  border: '1.5px solid var(--ce-settings-input-border)',
  borderRadius: '8px', fontSize: '0.925rem', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.15s',
  background: 'var(--ce-settings-input-bg)', color: 'var(--ce-settings-text)'
};
const labelStyle = {
  display: 'block', marginBottom: '0.4rem', fontWeight: '600',
  fontSize: '0.875rem', color: 'var(--ce-settings-label)'
};
const sectionCardStyle = {
  background: 'var(--ce-settings-card-bg)', borderRadius: '14px', padding: '2rem',
  boxShadow: 'var(--ce-settings-card-shadow)', marginBottom: '1.5rem'
};
const saveBtnStyle = {
  padding: '0.6rem 1.5rem',
  background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)',
  color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer',
  fontWeight: '600', fontSize: '0.9rem', marginTop: '1.25rem'
};

function formatDateDisplay(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const _addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const _toISO = (d) => d.toISOString().split('T')[0];

function getFirstPeriod(type, anchor, offset) {
  const day = anchor.getDate();
  if (type === 'semimonthly') {
    if (day <= 15) {
      const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const e = new Date(anchor.getFullYear(), anchor.getMonth(), 15);
      return { start: _toISO(s), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
    } else {
      const s = new Date(anchor.getFullYear(), anchor.getMonth(), 16);
      const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      return { start: _toISO(s), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
    }
  } else if (type === 'biweekly') {
    const e = _addDays(anchor, 13);
    return { start: _toISO(anchor), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
  } else if (type === 'weekly') {
    const e = _addDays(anchor, 6);
    return { start: _toISO(anchor), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
  } else if (type === 'monthly') {
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { start: _toISO(s), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
  }
  return null;
}

function getNextPeriod(type, prevEnd, offset) {
  const next = _addDays(new Date(prevEnd + 'T00:00:00'), 1);
  return getFirstPeriod(type, next, offset);
}

function generatePayPeriods(type, anchorDate, payDateOffsetDays, count = 6) {
  if (!anchorDate) return [];
  const anchor = new Date(anchorDate + 'T00:00:00');
  const offset = parseInt(payDateOffsetDays) || 5;
  const periods = [];
  const first = getFirstPeriod(type, anchor, offset);
  if (first) periods.push(first);
  while (periods.length < count) {
    const next = getNextPeriod(type, periods[periods.length - 1].end, offset);
    if (!next) break;
    periods.push(next);
  }
  return periods.slice(0, count);
}

// Maps UI lunch type to backend mode
const lunchTypeToMode = {
  'none': 'minutes',
  'unpaid': 'minutes',
  'paid': 'minutes',
  'fixed': 'dollars'
};

// Maps backend mode back to UI lunch type
const modeToLunchType = {
  'dollars': 'fixed',
  'minutes': 'unpaid'
};

export default function SettingsPage() {
  const { user, isSuperAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const token = user?.token || (() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored).token : null;
  })();
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const sectionRefs = {
    profile: useRef(null), organization: useRef(null), system: useRef(null),
    features: useRef(null),
    timeoff: useRef(null), notifications: useRef(null), integrations: useRef(null),
    payrollVisibility: useRef(null), subscription: useRef(null),
  };

  const [activeSection, setActiveSection] = useState('profile');
  const [status, setStatus] = useState({});
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('pro');
  const [subscriptionStatus, setSubscriptionStatus] = useState('trial');

  // Integrations state
  const [apiKey, setApiKey]               = useState(null);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [webhooks, setWebhooks]           = useState([]);
  const [webhookForm, setWebhookForm]     = useState({ url: '', description: '', events: [] });
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [integrationsEnabled, setIntegrationsEnabled] = useState(false);

  const WEBHOOK_EVENTS = [
    { value: 'clock.in',          label: 'Employee Clocks In' },
    { value: 'clock.out',         label: 'Employee Clocks Out' },
    { value: 'timeentry.created', label: 'Time Entry Created' },
    { value: 'timeentry.approved',label: 'Time Entry Approved' },
    { value: 'schedule.submitted',label: 'Schedule Submitted to Payroll' },
    { value: 'payroll.submitted', label: 'Payroll Submitted' },
  ];

  const [profile, setProfile] = useState({
    firstName: user?.firstName || '', lastName: user?.lastName || '',
    email: user?.email || '', currentPassword: '', newPassword: '', confirmPassword: ''
  });

  const [org, setOrg] = useState({
    companyName: '', timezone: 'America/New_York', logo: '', mileageRate: '0.70',
    labels: { employee: 'Employee', department: 'Department', manager: 'Manager', teamlead: 'Team Lead', timeEntry: 'Time Entry', payroll: 'Payroll' }
  });

  const [system, setSystem] = useState({
    payPeriod: 'semimonthly', weekStartDay: 'Monday',
    overtimeAfterDaily: '8', overtimeAfterWeekly: '40',
    requireApproval: false, overtimeEnabled: true,
    scheduleViewMode: 'payPeriod',
  });

  const [payPeriodConfig, setPayPeriodConfig] = useState({
    type: 'semimonthly',
    anchorDate: new Date().toISOString().split('T')[0],
    payDateOffsetDays: '5',
    overrides: {}
  });

  const [editingOverride, setEditingOverride] = useState(null);
  const [overrideDraft, setOverrideDraft] = useState({ start: '', end: '', payDate: '' });

  const [lunch, setLunch] = useState({
    defaultLunchType: 'unpaid',
    defaultLunchDuration: 30,
    lunchPayRate: 0,
    allowPerEntryOverride: true
  });

  const [notifications, setNotifications] = useState({
    schedulePublished: true, payrollReady: true, newTimeEntry: false,
    employeeAdded: true, weeklyReport: false
  });

  const [payrollVis, setPayrollVis] = useState({
    adminsCanSeeManagerPay:    false,
    adminsCanSeeOtherAdminPay: false,
    managersCanSeeAdminPay:    false,
  });
  const [allowCoOwnerToSeePay, setAllowCoOwnerToSeePay] = useState(false);

  const [orgFeatures, setOrgFeatures] = useState({
    clockInOut: true, timeEntry: true, scheduling: false, payroll: false,
    reporting: false, ptoTracking: false, mobileApp: false, evvCompliance: false,
  });

  const [ptoPolicy, setPtoPolicy] = useState({
    calculationType: 'frontload',
    hours: { fulltime: 80, parttime: 40, salaried: 80 },
    frontloadResetMonth: 1, accrualPeriod: 'biweekly', carryoverLimit: 0,
  });
  const [sickPolicy, setSickPolicy] = useState({
    calculationType: 'frontload',
    hours: { fulltime: 40, parttime: 20, salaried: 40 },
    frontloadResetMonth: 1, accrualPeriod: 'biweekly', carryoverLimit: 0,
  });

  const generatedPeriods = useMemo(() =>
    generatePayPeriods(payPeriodConfig.type, payPeriodConfig.anchorDate, payPeriodConfig.payDateOffsetDays, 6),
    [payPeriodConfig.type, payPeriodConfig.anchorDate, payPeriodConfig.payDateOffsetDays]
  );

  const mergedPeriods = useMemo(() => {
    if (!generatedPeriods.length) return [];
    const offset = parseInt(payPeriodConfig.payDateOffsetDays) || 5;
    const overrides = payPeriodConfig.overrides || {};
    const result = [];
    for (let i = 0; i < generatedPeriods.length; i++) {
      if (overrides[i]) {
        result.push({ ...overrides[i], isOverridden: true });
      } else if (i === 0) {
        result.push({ ...generatedPeriods[0], isOverridden: false });
      } else {
        const prev = result[i - 1];
        const next = getNextPeriod(payPeriodConfig.type, prev.end, offset);
        result.push({ ...(next || generatedPeriods[i]), isOverridden: false });
      }
    }
    return result;
  }, [generatedPeriods, payPeriodConfig.overrides, payPeriodConfig.type, payPeriodConfig.payDateOffsetDays]);

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const res = await fetch(`/api/organizations/${user?.organizationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOrg(prev => ({
            ...prev,
            companyName: data.name || data.companyName || '',
            timezone: data.timezone || 'America/New_York',
            mileageRate: (data.mileageFlatRate ?? data.mileageRate)?.toString() || '0',
            labels: { ...prev.labels, ...(data.labels || {}) }
          }));
          if (data.subscriptionPlan)  setCurrentPlan(data.subscriptionPlan);
          if (data.subscriptionStatus) setSubscriptionStatus(data.subscriptionStatus);
          if (data.apiKey) setApiKey(data.apiKey);
          setIntegrationsEnabled(data.features?.integrations || false);
          if (data.systemPreferences) setSystem(prev => ({ ...prev, ...data.systemPreferences }));
          if (data.notificationPreferences) setNotifications(prev => ({ ...prev, ...data.notificationPreferences }));
          if (data.payPeriodConfig) {
            setPayPeriodConfig(prev => ({
              ...prev,
              ...data.payPeriodConfig,
              overrides: data.payPeriodConfig.overrides || {}
            }));
          } else if (data.payPeriodType) {
            setPayPeriodConfig(prev => ({ ...prev, type: data.payPeriodType }));
          }

          if (data.ptoPolicy)  setPtoPolicy(p  => ({ ...p,  ...data.ptoPolicy  }));
          if (data.sickPolicy) setSickPolicy(p => ({ ...p,  ...data.sickPolicy }));
          if (data.features)   setOrgFeatures(p => ({ ...p, ...data.features }));
          if (data.payrollVisibility) setPayrollVis(p => ({ ...p, ...data.payrollVisibility }));

          // ── Lunch settings — map backend mode back to UI type ──
          if (data.lunchSettings) {
            setLunch(prev => ({
              ...prev,
              ...data.lunchSettings,
              defaultLunchType: data.lunchSettings.defaultLunchType
                || modeToLunchType[data.lunchSettings.mode]
                || 'unpaid',
              lunchPayRate: data.lunchSettings.lunchPayRate
                ?? data.lunchSettings.flatRatePerEntry
                ?? 0
            }));
          }
        }
      } catch (err) { console.error('Error fetching org:', err); }
    };
    if (user?.organizationId) fetchOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organizationId, token]);

  // Fetch co-owner pay toggle for superadmins
  useEffect(() => {
    if (!user?.id || !token || user?.role !== 'superadmin') return;
    fetch(`/api/employees/${user.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.allowCoOwnerToSeePay !== undefined) setAllowCoOwnerToSeePay(data.allowCoOwnerToSeePay); })
      .catch(() => {});
  }, [user?.id, token, user?.role]);

  // Fetch webhooks
  useEffect(() => {
    if (!user?.organizationId || !token) return;
    fetch('/api/webhooks', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setWebhooks(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [user?.organizationId, token]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && sectionRefs[hash]?.current) {
      setTimeout(() => {
        sectionRefs[hash]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(hash);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const scrollTo = (id) => {
    setActiveSection(id);
    sectionRefs[id]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const showStatus = (key, type, msg) => {
    setStatus(prev => ({ ...prev, [key]: { type, msg } }));
    setTimeout(() => setStatus(prev => ({ ...prev, [key]: null })), 3000);
  };

  const saveProfile = async () => {
    if (profile.newPassword && profile.newPassword !== profile.confirmPassword) {
      return showStatus('profile', 'error', 'New passwords do not match');
    }
    try {
      const body = { firstName: profile.firstName, lastName: profile.lastName, email: profile.email };
      if (profile.newPassword) {
        body.currentPassword = profile.currentPassword;
        body.newPassword = profile.newPassword;
      }
      if (user?.role === 'superadmin') body.allowCoOwnerToSeePay = allowCoOwnerToSeePay;
      const res = await fetch(`/api/employees/${user.id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
      if (res.ok) {
        showStatus('profile', 'success', 'Profile updated successfully');
        setProfile(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
      } else {
        const d = await res.json();
        showStatus('profile', 'error', d.message || 'Error updating profile');
      }
    } catch { showStatus('profile', 'error', 'Error updating profile'); }
  };

  const saveOrg = async () => {
    try {
      const res = await fetch(`/api/organizations/${user?.organizationId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          name: org.companyName,
          timezone: org.timezone,
          mileageRate: parseFloat(org.mileageRate),
          labels: org.labels,
          systemPreferences: system,
          notificationPreferences: notifications,
          lunchSettings: {
            ...lunch,
            // Map UI type → backend mode so SchedulePage can read it correctly
            mode: lunchTypeToMode[lunch.defaultLunchType] || 'minutes',
            flatRatePerEntry: parseFloat(lunch.lunchPayRate) || 0
          },
          payPeriodConfig,
          ptoPolicy,
          sickPolicy,
          features: orgFeatures,
        })
      });
      if (res.ok) showStatus('org', 'success', 'Organization settings saved');
      else showStatus('org', 'error', 'Error saving organization settings');
    } catch { showStatus('org', 'error', 'Error saving settings'); }
  };

  const savePayrollVis = async () => {
    try {
      const res = await fetch(`/api/organizations/${user?.organizationId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ payrollVisibility: payrollVis })
      });
      if (res.ok) showStatus('payrollVis', 'success', 'Payroll visibility settings saved');
      else showStatus('payrollVis', 'error', 'Error saving payroll visibility');
    } catch { showStatus('payrollVis', 'error', 'Error saving payroll visibility'); }
  };

  const handlePlanChange = async (planId) => {
    try {
      const res = await fetch('/api/subscriptions/create-checkout', {
        method: 'POST',
        headers,
        body: JSON.stringify({ planKey: planId }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        showStatus('subscription', 'error', data.message || 'Error starting checkout');
      }
    } catch {
      showStatus('subscription', 'error', 'Could not connect to billing');
    }
  };

  const startOverride = (i, period) => {
    setEditingOverride(i);
    setOverrideDraft({ start: period.start, end: period.end, payDate: period.payDate });
  };

  const saveOverride = (i) => {
    const draft = { ...overrideDraft };
    if (draft.end) {
      const endDate = new Date(draft.end + 'T00:00:00');
      const offset = parseInt(payPeriodConfig.payDateOffsetDays) || 5;
      const autoPayDate = new Date(endDate);
      autoPayDate.setDate(autoPayDate.getDate() + offset);
      draft.payDate = autoPayDate.toISOString().split('T')[0];
    }
    setPayPeriodConfig(prev => ({
      ...prev,
      overrides: { ...prev.overrides, [i]: draft }
    }));
    setEditingOverride(null);
  };

  const clearOverride = (i) => {
    setPayPeriodConfig(prev => {
      const newOverrides = { ...prev.overrides };
      delete newOverrides[i];
      return { ...prev, overrides: newOverrides };
    });
    setEditingOverride(null);
  };

  const visibleSections = SECTIONS.filter(s => s.roles.includes(user?.role));

  const StatusMsg = ({ k }) => status[k] ? (
    <p style={{ marginTop: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: status[k].type === 'success' ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
      {status[k].type === 'success' ? <IcoCheck size={12} color="#059669"/> : <IcoX size={12} color="#ef4444"/>} {status[k].msg}
    </p>
  ) : null;

  const PLAN_DISPLAY = {
    starter:      { name: 'Starter',      color: '#22c55e', bg: '#eff6ff', border: '#bfdbfe', desc: '1–30 employees (cap 34) · $29/mo' },
    professional: { name: 'Professional', color: '#22c55e', bg: '#eef2ff', border: '#c7d2fe', desc: '31–100 employees (cap 115) · $69/mo' },
    growth:       { name: 'Growth',       color: '#7c3aed', bg: '#f5f3ff', border: '#c4b5fd', desc: '101–150 employees (cap 172) · $149/mo' },
    enterprise:   { name: 'Enterprise',   color: '#0f172a', bg: '#f8fafc', border: '#cbd5e1', desc: '151+ employees · Custom pricing' },
  };
  const planDisplay = PLAN_DISPLAY[currentPlan] || PLAN_DISPLAY.starter;

  const TIMEZONES = [
    'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'America/Phoenix'
  ];

  const PAY_PERIOD_TYPES = [
    { value: 'weekly', label: 'Weekly', desc: 'Every 7 days' },
    { value: 'biweekly', label: 'Bi-Weekly', desc: 'Every 14 days' },
    { value: 'semimonthly', label: 'Semi-Monthly', desc: '1st–15th & 16th–end of month' },
    { value: 'monthly', label: 'Monthly', desc: 'Once per month' },
  ];

  // Features available per plan — mirrors server/middleware/featureGate.js PLAN_FEATURES
  const PLAN_FEATURES = {
    starter:      ['clockInOut', 'timeEntry', 'payroll', 'scheduling', 'ptoTracking', 'mobileApp'],
    professional: ['clockInOut', 'timeEntry', 'payroll', 'scheduling', 'ptoTracking', 'mobileApp', 'reporting', 'evvCompliance'],
    growth:       ['clockInOut', 'timeEntry', 'payroll', 'scheduling', 'ptoTracking', 'mobileApp', 'reporting', 'evvCompliance'],
    enterprise:   ['clockInOut', 'timeEntry', 'payroll', 'scheduling', 'ptoTracking', 'mobileApp', 'reporting', 'evvCompliance', 'integrations'],
  };
  const isTrial    = subscriptionStatus === 'trial';
  const planAllows = (feature) => isTrial || (PLAN_FEATURES[currentPlan] || PLAN_FEATURES.starter).includes(feature);

  const Toggle = ({ value, onChange, label, desc, featureKey }) => {
    const locked = featureKey ? !planAllows(featureKey) : false;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', opacity: locked ? 0.65 : 1 }}>
        <div
          onClick={!locked ? onChange : undefined}
          style={{ width: '44px', height: '24px', borderRadius: '99px', cursor: locked ? 'not-allowed' : 'pointer', background: locked ? '#e5e7eb' : (value ? 'linear-gradient(135deg, #22c55e, #0f4d2e)' : '#d1d5db'), position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
        >
          <div style={{ position: 'absolute', top: '3px', left: (!locked && value) ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ ...labelStyle, margin: 0 }}>{label}</label>
            {locked && (
              <span style={{ fontSize: '0.7rem', background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', padding: '0.1rem 0.45rem', borderRadius: '4px', fontWeight: '700', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                <IcoLock size={13}/> Upgrade
              </span>
            )}
          </div>
          {desc && <p style={{ fontSize: '0.775rem', color: 'var(--ce-settings-subtext)', margin: 0 }}>{desc}</p>}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <section className="welcome-section">
          <h1 style={{display:'flex',alignItems:'center',gap:'0.5rem'}}><IcoSettings size={24} color="#22c55e"/> Settings</h1>
          <p className="subtitle">Manage your account, organization, and preferences</p>
        </section>

        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>

          {/* Sidebar nav */}
          <div style={{ width: '210px', flexShrink: 0, background: 'white', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', position: 'sticky', top: '1rem' }}>
            {visibleSections.map(s => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 0.875rem', marginBottom: '0.25rem', background: activeSection === s.id ? '#e0e7ff' : 'none', color: activeSection === s.id ? '#4338ca' : '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: activeSection === s.id ? '700' : '500', fontSize: '0.875rem', transition: 'all 0.15s' }}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>

            {/* ── Profile ── */}
            <div ref={sectionRefs.profile} style={sectionCardStyle}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem', color: 'var(--ce-settings-text)' }}>Profile Settings</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelStyle}>First Name</label>
                  <input style={inputStyle} value={profile.firstName} onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>Last Name</label>
                  <input style={inputStyle} value={profile.lastName} onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Email</label>
                <input style={inputStyle} type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '1.25rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '1rem', color: 'var(--ce-settings-label)' }}>Change Password</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                  <div>
                    <label style={labelStyle}>Current Password</label>
                    <input style={inputStyle} type="password" value={profile.currentPassword} onChange={e => setProfile(p => ({ ...p, currentPassword: e.target.value }))} placeholder="Enter current password" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>New Password</label>
                      <input style={inputStyle} type="password" value={profile.newPassword} onChange={e => setProfile(p => ({ ...p, newPassword: e.target.value }))} placeholder="Min 8 characters" />
                    </div>
                    <div>
                      <label style={labelStyle}>Confirm New Password</label>
                      <input style={inputStyle} type="password" value={profile.confirmPassword} onChange={e => setProfile(p => ({ ...p, confirmPassword: e.target.value }))} placeholder="Re-enter password" />
                    </div>
                  </div>
                </div>
              </div>
              {isSuperAdmin() && user?.role === 'superadmin' && (
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '1.25rem', marginTop: '0.25rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--ce-settings-label)' }}>Co-Owner Pay Visibility</h3>
                  <p style={{ fontSize: '0.8rem', color: 'var(--ce-settings-subtext)', marginBottom: '1rem' }}>
                    If your organization has multiple owners, control whether they can see your personal pay.
                  </p>
                  <Toggle
                    value={allowCoOwnerToSeePay}
                    onChange={() => setAllowCoOwnerToSeePay(v => !v)}
                    label="Allow other owners to see my pay"
                    desc="Other superadmin accounts in your organization will be able to view your hourly rate and pay totals."
                  />
                </div>
              )}
              <button onClick={saveProfile} style={saveBtnStyle}>Save Profile</button>
              <StatusMsg k="profile" />
            </div>

            {/* ── Organization & Labels ── */}
            {isSuperAdmin() && (
              <div ref={sectionRefs.organization} style={sectionCardStyle}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem', color: 'var(--ce-settings-text)' }}>Organization & Custom Labels</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={labelStyle}>Company Name</label>
                    <input style={inputStyle} value={org.companyName} onChange={e => setOrg(p => ({ ...p, companyName: e.target.value }))} />
                  </div>
                  <div>
                    <label htmlFor="setting-timezone" style={labelStyle}>Timezone</label>
                    <select id="setting-timezone" style={inputStyle} value={org.timezone} onChange={e => setOrg(p => ({ ...p, timezone: e.target.value }))}>
                      {TIMEZONES.map(tz => (
                        <option key={tz} value={tz}>{tz.replace('America/', '').replace('Pacific/', 'Pacific/').replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Mileage Reimbursement Rate ($/mile)</label>
                    <input style={inputStyle} type="number" step="0.01" min="0" value={org.mileageRate} onChange={e => setOrg(p => ({ ...p, mileageRate: e.target.value }))} />
                  </div>
                </div>

                {/* Custom Labels */}
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '1.25rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--ce-settings-label)' }}>Custom Labels</h3>
                  <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1.25rem' }}>Rename default terms to match your organization's language.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {Object.entries(org.labels).map(([key, val]) => (
                      <div key={key}>
                        <label style={labelStyle}>
                          <span style={{ color: 'var(--ce-settings-subtext)', fontWeight: '400' }}>"{key.charAt(0).toUpperCase() + key.slice(1)}" →</span> Custom Label
                        </label>
                        <input
                          style={inputStyle}
                          value={val}
                          placeholder={key.charAt(0).toUpperCase() + key.slice(1)}
                          onChange={e => setOrg(p => ({ ...p, labels: { ...p.labels, [key]: e.target.value } }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Lunch Settings */}
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--ce-settings-label)' }}>Lunch Settings</h3>
                  <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1.25rem' }}>Set the default lunch policy for your organization.</p>

                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={labelStyle}>Default Lunch Type</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                      {[
                        { value: 'none', label: 'No Lunch', desc: 'Skip lunch entirely' },
                        { value: 'unpaid', label: 'Unpaid', desc: 'Deduct from hours' },
                        { value: 'paid', label: 'Paid', desc: 'No deduction' },
                        { value: 'fixed', label: 'Fixed Rate', desc: 'Custom pay rate' }
                      ].map(({ value, label, desc }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setLunch(p => ({ ...p, defaultLunchType: value }))}
                          style={{ padding: '0.65rem 0.5rem', background: lunch.defaultLunchType === value ? '#e0e7ff' : 'white', color: lunch.defaultLunchType === value ? '#4338ca' : '#6b7280', border: `2px solid ${lunch.defaultLunchType === value ? '#818cf8' : '#e5e7eb'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', textAlign: 'center', transition: 'all 0.15s' }}
                        >
                          <div>{label}</div>
                          <div style={{ fontSize: '0.68rem', fontWeight: '400', marginTop: '0.15rem', opacity: 0.75 }}>{desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    {lunch.defaultLunchType !== 'none' && (
                      <div>
                        <label style={labelStyle}>Default Duration (minutes)</label>
                        <input
                          style={inputStyle}
                          type="number"
                          min="0"
                          value={lunch.defaultLunchDuration}
                          onChange={e => setLunch(p => ({ ...p, defaultLunchDuration: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                    )}
                    {lunch.defaultLunchType === 'fixed' && (
                      <div>
                        <label style={labelStyle}>Fixed Lunch Pay Rate ($/hr)</label>
                        <input
                          style={inputStyle}
                          type="number"
                          step="0.01"
                          min="0"
                          value={lunch.lunchPayRate}
                          onChange={e => setLunch(p => ({ ...p, lunchPayRate: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                    )}
                  </div>

                  <Toggle
                    value={lunch.allowPerEntryOverride}
                    onChange={() => setLunch(p => ({ ...p, allowPerEntryOverride: !p.allowPerEntryOverride }))}
                    label="Allow Per-Entry Override"
                    desc="Managers can change lunch type when submitting individual time entries"
                  />
                </div>

                <button onClick={saveOrg} style={saveBtnStyle}>Save Organization Settings</button>
                <StatusMsg k="org" />
              </div>
            )}

            {/* ── System Preferences ── */}
            {isSuperAdmin() && (
              <div ref={sectionRefs.system} style={sectionCardStyle}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem', color: 'var(--ce-settings-text)' }}>System Preferences</h2>

                {/* Pay Period Configuration */}
                <div style={{ marginBottom: '1.75rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--ce-settings-label)', marginBottom: '0.25rem' }}>Pay Period Configuration</h3>
                  <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1.25rem' }}>
                    Set your pay period type and anchor date. The system auto-generates all periods — you can override any individual period below.
                  </p>

                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={labelStyle}>Pay Period Type</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                      {PAY_PERIOD_TYPES.map(({ value, label, desc }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setPayPeriodConfig(p => ({ ...p, type: value, overrides: {} }))}
                          style={{ padding: '0.75rem 0.5rem', background: payPeriodConfig.type === value ? '#e0e7ff' : 'white', color: payPeriodConfig.type === value ? '#4338ca' : '#6b7280', border: `2px solid ${payPeriodConfig.type === value ? '#818cf8' : '#e5e7eb'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', textAlign: 'center', transition: 'all 0.15s' }}
                        >
                          <div>{label}</div>
                          <div style={{ fontSize: '0.68rem', fontWeight: '400', marginTop: '0.2rem', opacity: 0.75 }}>{desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <label style={labelStyle}>Pay Period Start Date</label>
                      <input
                        style={inputStyle}
                        type="date"
                        value={payPeriodConfig.anchorDate}
                        onChange={e => setPayPeriodConfig(p => ({ ...p, anchorDate: e.target.value, overrides: {} }))}
                      />
                      <p style={{ fontSize: '0.75rem', color: 'var(--ce-settings-subtext)', marginTop: '0.3rem' }}>Changing this clears all overrides.</p>
                    </div>
                    <div>
                      <label style={labelStyle}>Pay Date (days after period ends)</label>
                      <input
                        style={inputStyle}
                        type="number"
                        min="0"
                        max="30"
                        value={payPeriodConfig.payDateOffsetDays}
                        onChange={e => setPayPeriodConfig(p => ({ ...p, payDateOffsetDays: e.target.value }))}
                      />
                      <p style={{ fontSize: '0.75rem', color: 'var(--ce-settings-subtext)', marginTop: '0.3rem' }}>e.g. 5 = checks issued 5 days after period ends</p>
                    </div>
                  </div>

                  {mergedPeriods.length > 0 && (
                    <div style={{ background: '#f8f9ff', borderRadius: '10px', padding: '1rem', border: '1px solid #e0e7ff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--ce-settings-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                          Pay Periods — Click Override to Customize
                        </p>
                        {Object.keys(payPeriodConfig.overrides || {}).length > 0 && (
                          <button
                            type="button"
                            onClick={() => setPayPeriodConfig(p => ({ ...p, overrides: {} }))}
                            style={{ fontSize: '0.72rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                          >
                            Clear all overrides
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {mergedPeriods.map((period, i) => {
                          const isEditing = editingOverride === i;
                          return (
                            <div key={i} style={{ background: 'white', borderRadius: '8px', border: `1.5px solid ${period.isOverridden ? '#f59e0b' : '#e5e7eb'}`, overflow: 'hidden' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0.5rem', padding: '0.65rem 0.875rem', alignItems: 'center', fontSize: '0.82rem' }}>
                                <div>
                                  <span style={{ fontSize: '0.68rem', fontWeight: '700', color: 'var(--ce-settings-subtext)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>Period Start</span>
                                  <span style={{ fontWeight: '600', color: 'var(--ce-settings-text)' }}>{formatDateDisplay(period.start)}</span>
                                </div>
                                <div>
                                  <span style={{ fontSize: '0.68rem', fontWeight: '700', color: 'var(--ce-settings-subtext)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>Period End</span>
                                  <span style={{ fontWeight: '600', color: 'var(--ce-settings-text)' }}>{formatDateDisplay(period.end)}</span>
                                </div>
                                <div>
                                  <span style={{ fontSize: '0.68rem', fontWeight: '700', color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>
                                    Pay Date {period.isOverridden && (
                                      <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '0 4px', fontSize: '0.6rem', marginLeft: '2px' }}>CUSTOM</span>
                                    )}
                                  </span>
                                  <span style={{ fontWeight: '700', color: '#059669' }}>{formatDateDisplay(period.payDate)}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                  {period.isOverridden && !isEditing && (
                                    <button
                                      type="button"
                                      onClick={() => clearOverride(i)}
                                      style={{ padding: '0.25rem 0.5rem', background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '600' }}
                                    >
                                      Reset
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => isEditing ? setEditingOverride(null) : startOverride(i, period)}
                                    style={{ padding: '0.25rem 0.6rem', background: isEditing ? '#4b5563' : '#e0e7ff', color: isEditing ? 'white' : '#4338ca', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '600' }}
                                  >
                                    {isEditing ? 'Cancel' : 'Override'}
                                  </button>
                                </div>
                              </div>

                              {isEditing && (
                                <div style={{ padding: '0.875rem', borderTop: '1px solid #f3f4f6', background: '#fffbeb', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                  <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: '600', color: '#92400e' }}>Set custom dates for this period</p>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                                    <div>
                                      <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Period Start</label>
                                      <input
                                        type="date"
                                        value={overrideDraft.start}
                                        onChange={e => setOverrideDraft(d => ({ ...d, start: e.target.value }))}
                                        style={{ ...inputStyle, fontSize: '0.82rem', padding: '0.45rem 0.6rem' }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Period End</label>
                                      <input
                                        type="date"
                                        value={overrideDraft.end}
                                        onChange={e => {
                                          const newEnd = e.target.value;
                                          const offset = parseInt(payPeriodConfig.payDateOffsetDays) || 5;
                                          const autoPayDate = new Date(newEnd + 'T00:00:00');
                                          autoPayDate.setDate(autoPayDate.getDate() + offset);
                                          setOverrideDraft(d => ({
                                            ...d,
                                            end: newEnd,
                                            payDate: autoPayDate.toISOString().split('T')[0]
                                          }));
                                        }}
                                        style={{ ...inputStyle, fontSize: '0.82rem', padding: '0.45rem 0.6rem' }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Pay Date</label>
                                      <input
                                        type="date"
                                        value={overrideDraft.payDate}
                                        onChange={e => setOverrideDraft(d => ({ ...d, payDate: e.target.value }))}
                                        style={{ ...inputStyle, fontSize: '0.82rem', padding: '0.45rem 0.6rem' }}
                                      />
                                    </div>
                                  </div>
                                  <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--ce-settings-subtext)' }}>Pay date auto-updates when you change the end date. You can still adjust it manually.</p>
                                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                      type="button"
                                      onClick={() => saveOverride(i)}
                                      style={{ padding: '0.4rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600' }}
                                    >
                                      Save Override
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingOverride(null)}
                                      style={{ padding: '0.4rem 0.875rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600' }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label htmlFor="setting-week-start" style={labelStyle}>Week Starts On</label>
                    <select id="setting-week-start" style={inputStyle} value={system.weekStartDay || 'Monday'} onChange={e => setSystem(p => ({ ...p, weekStartDay: e.target.value }))}>
                      <option value="Monday">Monday</option>
                      <option value="Sunday">Sunday</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="setting-schedule-view" style={labelStyle}>Schedule Page View</label>
                    <select id="setting-schedule-view" style={inputStyle} value={system.scheduleViewMode || 'payPeriod'} onChange={e => setSystem(p => ({ ...p, scheduleViewMode: e.target.value }))}>
                      <option value="payPeriod">Full Pay Period (recommended)</option>
                      <option value="week">Week-by-Week (legacy)</option>
                    </select>
                    <p style={{ fontSize: '0.75rem', color: 'var(--ce-settings-subtext)', marginTop: '0.3rem' }}>
                      Full pay period shows every day being paid out at once (more columns for biweekly/semimonthly/monthly). Week-by-week pages one 7-day week at a time.
                    </p>
                  </div>
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <Toggle
                    value={system.overtimeEnabled}
                    onChange={() => setSystem(p => ({ ...p, overtimeEnabled: !p.overtimeEnabled }))}
                    label="Enable Overtime Calculations"
                  />
                  {system.overtimeEnabled && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', paddingLeft: '0.5rem', marginTop: '1rem' }}>
                      <div>
                        <label style={labelStyle}>Overtime After (daily hours)</label>
                        <input style={inputStyle} type="number" min="1" max="24" value={system.overtimeAfterDaily} onChange={e => setSystem(p => ({ ...p, overtimeAfterDaily: e.target.value }))} />
                      </div>
                      <div>
                        <label style={labelStyle}>Overtime After (weekly hours)</label>
                        <input style={inputStyle} type="number" min="1" max="168" value={system.overtimeAfterWeekly} onChange={e => setSystem(p => ({ ...p, overtimeAfterWeekly: e.target.value }))} />
                      </div>
                    </div>
                  )}
                </div>

                <Toggle
                  value={system.requireApproval}
                  onChange={() => setSystem(p => ({ ...p, requireApproval: !p.requireApproval }))}
                  label="Require Manager Approval for Time Entries"
                  desc="Managers must approve time entries before they flow into payroll"
                />

                {/* ── Appearance ── */}
                <div style={{ borderTop: '1px solid var(--ce-card-border)', paddingTop: '1.5rem', marginTop: '1rem', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--ce-settings-text)', marginBottom: '0.25rem' }}>Appearance</h3>
                  <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1rem' }}>
                    Choose your preferred color theme. This setting is saved per browser.
                  </p>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    {[{ value: 'light', icon: <IcoSun size={16}/>, label: 'Light' }, { value: 'dark', icon: <IcoMoon size={16}/>, label: 'Dark' }].map(({ value, icon, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => theme !== value && toggleTheme()}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.6rem 1.25rem',
                          border: `2px solid ${theme === value ? '#22c55e' : 'var(--ce-settings-input-border)'}`,
                          borderRadius: '8px',
                          background: theme === value ? 'rgba(34,197,94,0.12)' : 'var(--ce-settings-input-bg)',
                          color: theme === value ? '#22c55e' : 'var(--ce-settings-text)',
                          fontWeight: '600', fontSize: '0.9rem',
                          cursor: theme === value ? 'default' : 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <span>{icon}</span><span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <button onClick={saveOrg} style={saveBtnStyle}>Save System Preferences</button>
                <StatusMsg k="org" />
              </div>
            )}

            {/* ── Features ── */}
            {isSuperAdmin() && (
              <div ref={sectionRefs.features} style={sectionCardStyle}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--ce-settings-text)' }}>Features</h2>
                <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1.75rem' }}>
                  Enable or disable modules for your organization. Changes take effect immediately after saving. Nav links and dashboard shortcuts for disabled features will be hidden from all users.
                </p>
                {!isTrial && currentPlan === 'starter' && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: '#fef9ec', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '0.825rem', color: '#92400e' }}>
                    Reporting and EVV Compliance require a <strong>Professional</strong> plan. <a href="/settings#subscription" style={{ color: '#d97706', fontWeight: '700' }}>Upgrade</a> to unlock — also removes the 25-employee cap.
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <Toggle
                    featureKey="clockInOut"
                    value={orgFeatures.clockInOut}
                    onChange={() => setOrgFeatures(p => ({ ...p, clockInOut: !p.clockInOut }))}
                    label="Clock In / Out"
                    desc="Mobile and web clock-in, time entry management"
                  />
                  <Toggle
                    featureKey="timeEntry"
                    value={orgFeatures.timeEntry}
                    onChange={() => setOrgFeatures(p => ({ ...p, timeEntry: !p.timeEntry }))}
                    label="Time Entry"
                    desc="Manual time card entry for REG, OT, PTO, sick, and other hour types"
                  />
                  <Toggle
                    featureKey="scheduling"
                    value={orgFeatures.scheduling}
                    onChange={() => setOrgFeatures(p => ({ ...p, scheduling: !p.scheduling }))}
                    label="Scheduling"
                    desc="Staff scheduling calendar and shift management"
                  />
                  <Toggle
                    featureKey="payroll"
                    value={orgFeatures.payroll}
                    onChange={() => setOrgFeatures(p => ({ ...p, payroll: !p.payroll }))}
                    label="Payroll"
                    desc="Payroll processing, reports, and pay period management"
                  />
                  <Toggle
                    featureKey="reporting"
                    value={orgFeatures.reporting}
                    onChange={() => setOrgFeatures(p => ({ ...p, reporting: !p.reporting }))}
                    label="Reporting"
                    desc="Analytics, labor cost reports, and data exports"
                  />
                  <Toggle
                    featureKey="ptoTracking"
                    value={orgFeatures.ptoTracking}
                    onChange={() => setOrgFeatures(p => ({ ...p, ptoTracking: !p.ptoTracking }))}
                    label="PTO Tracking"
                    desc="Paid time off requests, balances, and accrual"
                  />
                  <Toggle
                    featureKey="mobileApp"
                    value={orgFeatures.mobileApp}
                    onChange={() => setOrgFeatures(p => ({ ...p, mobileApp: !p.mobileApp }))}
                    label="Mobile App"
                    desc="Employee mobile access for clocking in and viewing schedules"
                  />
                  <Toggle
                    featureKey="evvCompliance"
                    value={orgFeatures.evvCompliance}
                    onChange={() => setOrgFeatures(p => ({ ...p, evvCompliance: !p.evvCompliance }))}
                    label="EVV Compliance"
                    desc="Electronic Visit Verification for home health and caregiving agencies"
                  />
                </div>
                <button onClick={saveOrg} style={saveBtnStyle}>Save Features</button>
                <StatusMsg k="org" />
              </div>
            )}

            {/* ── Time Off Policy ── */}
            {isSuperAdmin() && (
              <div ref={sectionRefs.timeoff} style={sectionCardStyle}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--ce-settings-text)' }}>Time Off Policy</h2>
                <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1.75rem' }}>
                  Set how PTO and sick time are calculated for your entire organization. Changes apply to new balance records going forward.
                </p>

                <SettingsPolicySection
                  title="PTO"
                  policy={ptoPolicy}
                  onChange={patch => setPtoPolicy(p => ({ ...p, ...patch }))}
                  inputStyle={inputStyle}
                  labelStyle={labelStyle}
                />

                <div style={{ borderTop: '1px solid #f3f4f6', margin: '1.5rem 0' }} />

                <SettingsPolicySection
                  title="Sick Time"
                  policy={sickPolicy}
                  onChange={patch => setSickPolicy(p => ({ ...p, ...patch }))}
                  inputStyle={inputStyle}
                  labelStyle={labelStyle}
                />

                <button onClick={saveOrg} style={saveBtnStyle}>Save Time Off Policy</button>
                <StatusMsg k="org" />
              </div>
            )}

            {/* ── Notifications ── */}
            <div ref={sectionRefs.notifications} style={sectionCardStyle}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--ce-settings-text)' }}>Notification Preferences</h2>
              <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1.5rem' }}>Choose which email notifications you receive</p>
              {[
                { key: 'schedulePublished', label: 'Schedule Published', desc: 'When a new schedule is saved for your team' },
                { key: 'payrollReady', label: 'Payroll Ready', desc: 'When payroll is calculated and ready to review' },
                { key: 'newTimeEntry', label: 'New Time Entry Submitted', desc: 'When an employee submits a time entry' },
                { key: 'employeeAdded', label: 'Employee Added', desc: 'When a new employee is added to your organization' },
                { key: 'weeklyReport', label: 'Weekly Summary Report', desc: 'Weekly digest of hours, payroll, and activity' },
              ].map(({ key, label, desc }) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.875rem 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '0.9rem', color: 'var(--ce-settings-text)' }}>{label}</p>
                    <p style={{ margin: 0, fontSize: '0.775rem', color: 'var(--ce-settings-subtext)' }}>{desc}</p>
                  </div>
                  <div
                    onClick={() => setNotifications(p => ({ ...p, [key]: !p[key] }))}
                    style={{ width: '44px', height: '24px', borderRadius: '99px', cursor: 'pointer', flexShrink: 0, background: notifications[key] ? 'linear-gradient(135deg, #22c55e, #0f4d2e)' : '#d1d5db', position: 'relative', transition: 'background 0.2s' }}
                  >
                    <div style={{ position: 'absolute', top: '3px', left: notifications[key] ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </div>
              ))}
              <button onClick={saveOrg} style={saveBtnStyle}>Save Notification Preferences</button>
              <StatusMsg k="notifications" />
            </div>

            {/* ── Integrations & API ── */}
            {isSuperAdmin() && (
              <div ref={sectionRefs.integrations} style={sectionCardStyle}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--ce-settings-text)' }}>Integrations & API</h2>
                <p style={{ fontSize: '0.875rem', color: 'var(--ce-settings-subtext)', marginBottom: '1.5rem' }}>
                  Connect ClockEarnings to ADP, QuickBooks, Zapier, home health platforms, and more via webhooks and your API key.
                  One-time add-on — pay once, use forever.
                </p>

                {!integrationsEnabled ? (
                  <div style={{ padding: '1.75rem', background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)', border: '1.5px solid #c7d2fe', borderRadius: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                      <div>
                        <p style={{ margin: '0 0 0.35rem', fontWeight: '800', fontSize: '1.1rem', color: '#1e1b4b' }}>Integrations Add-On</p>
                        <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: '#4338ca' }}>
                          Webhooks, REST API access, and connections to ADP, QuickBooks, Zapier &amp; more.
                        </p>
                        <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.825rem', color: 'var(--ce-settings-label)', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          {['Real-time webhooks on clock events, payroll, and approvals', 'Secure API key for pulling data into any system', 'ADP-compatible export (one-click)', 'Connect Zapier, Slack, QuickBooks, home health platforms'].map(f => (
                            <li key={f}>{f}</li>
                          ))}
                        </ul>
                      </div>
                      <div style={{ textAlign: 'center', flexShrink: 0 }}>
                        <p style={{ margin: '0 0 0.25rem', fontSize: '1.75rem', fontWeight: '900', color: '#4338ca' }}>One-time fee</p>
                        <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: 'var(--ce-settings-subtext)' }}>No recurring charge · Permanent access</p>
                        <button
                          onClick={async () => {
                            const r = await fetch('/api/subscriptions/purchase-integrations', { method: 'POST', headers });
                            const d = await r.json();
                            if (r.ok && d.url) window.location.href = d.url;
                            else alert(d.message || 'Error starting checkout');
                          }}
                          style={{ padding: '0.75rem 1.75rem', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', boxShadow: '0 4px 16px rgba(79,70,229,0.35)' }}
                        >
                          Purchase Integrations →
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                    {/* API Key */}
                    <div style={{ padding: '1.25rem', background: '#f9fafb', borderRadius: '10px', border: '1.5px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div>
                          <p style={{ margin: 0, fontWeight: '700', fontSize: '0.95rem', color: 'var(--ce-settings-text)' }}>API Key</p>
                          <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: 'var(--ce-settings-subtext)' }}>Use this key to authenticate API requests from external systems.</p>
                        </div>
                        <button
                          onClick={async () => {
                            const r = await fetch(`/api/organizations/${user?.organizationId}/generate-api-key`, { method: 'POST', headers });
                            if (r.ok) { const d = await r.json(); setApiKey(d.apiKey); setApiKeyVisible(true); }
                          }}
                          style={{ padding: '0.45rem 0.875rem', background: '#1e2a4a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap' }}
                        >
                          {apiKey ? '↻ Regenerate' : '+ Generate Key'}
                        </button>
                      </div>
                      {apiKey && (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <code style={{ flex: 1, padding: '0.6rem 0.875rem', background: 'white', border: '1.5px solid #e5e7eb', borderRadius: '6px', fontSize: '0.82rem', color: 'var(--ce-settings-label)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {apiKeyVisible ? apiKey : apiKey.slice(0, 12) + '•'.repeat(20)}
                          </code>
                          <button onClick={() => setApiKeyVisible(v => !v)} style={{ padding: '0.5rem 0.75rem', background: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600', color: 'var(--ce-settings-label)' }}>
                            {apiKeyVisible ? 'Hide' : 'Show'}
                          </button>
                          <button onClick={() => navigator.clipboard.writeText(apiKey)} style={{ padding: '0.5rem 0.75rem', background: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600', color: 'var(--ce-settings-label)' }}>
                            Copy
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Webhooks */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <div>
                          <p style={{ margin: 0, fontWeight: '700', fontSize: '0.95rem', color: 'var(--ce-settings-text)' }}>Webhooks</p>
                          <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: 'var(--ce-settings-subtext)' }}>Send real-time event data to any URL.</p>
                        </div>
                        <button onClick={() => setShowWebhookForm(true)} style={{ padding: '0.45rem 0.875rem', background: 'linear-gradient(135deg, #22c55e, #0f4d2e)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>
                          + Add Webhook
                        </button>
                      </div>

                      {/* Add webhook form */}
                      {showWebhookForm && (
                        <div style={{ padding: '1.25rem', background: '#f0f4ff', border: '1.5px solid #c7d2fe', borderRadius: '10px', marginBottom: '1rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                            <div>
                              <label style={{ ...labelStyle }}>Endpoint URL *</label>
                              <input type="url" placeholder="https://your-server.com/webhook" value={webhookForm.url} onChange={e => setWebhookForm(f => ({ ...f, url: e.target.value }))} style={{ ...inputStyle }} />
                            </div>
                            <div>
                              <label style={{ ...labelStyle }}>Description (optional)</label>
                              <input type="text" placeholder="e.g. Notify Slack on clock events" value={webhookForm.description} onChange={e => setWebhookForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle }} />
                            </div>
                            <div>
                              <label style={{ ...labelStyle }}>Events to subscribe to *</label>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {WEBHOOK_EVENTS.map(ev => {
                                  const on = webhookForm.events.includes(ev.value);
                                  return (
                                    <button key={ev.value} type="button"
                                      onClick={() => setWebhookForm(f => ({ ...f, events: on ? f.events.filter(e => e !== ev.value) : [...f.events, ev.value] }))}
                                      style={{ padding: '0.3rem 0.75rem', borderRadius: '99px', border: on ? '2px solid #22c55e' : '2px solid #e5e7eb', background: on ? '#ede9fe' : '#f9fafb', color: on ? '#4c1d95' : '#374151', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer' }}>
                                      {on && <IcoCheck size={11} color="#22c55e" style={{marginRight:3}}/>}{ev.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                              <button type="button"
                                onClick={async () => {
                                  if (!webhookForm.url || !webhookForm.events.length) return;
                                  const r = await fetch('/api/webhooks', { method: 'POST', headers, body: JSON.stringify(webhookForm) });
                                  if (r.ok) { const d = await r.json(); setWebhooks(prev => [d, ...prev]); setWebhookForm({ url: '', description: '', events: [] }); setShowWebhookForm(false); }
                                }}
                                style={{ padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #22c55e, #0f4d2e)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
                                Save Webhook
                              </button>
                              <button type="button" onClick={() => { setShowWebhookForm(false); setWebhookForm({ url: '', description: '', events: [] }); }} style={{ padding: '0.6rem 1.25rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Webhook list */}
                      {webhooks.length === 0 ? (
                        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--ce-settings-subtext)', fontSize: '0.875rem', background: '#f9fafb', borderRadius: '10px', border: '1.5px dashed #e5e7eb' }}>
                          No webhooks yet. Add one to start receiving real-time events.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {webhooks.map(wh => (
                            <div key={wh._id} style={{ padding: '1rem 1.25rem', background: 'white', border: '1.5px solid #e5e7eb', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: wh.active ? '#10b981' : '#9ca3af', flexShrink: 0 }} />
                                  <code style={{ fontSize: '0.82rem', color: 'var(--ce-settings-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wh.url}</code>
                                </div>
                                {wh.description && <p style={{ margin: '0 0 0.4rem', fontSize: '0.78rem', color: 'var(--ce-settings-subtext)' }}>{wh.description}</p>}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                                  {wh.events.map(ev => (
                                    <span key={ev} style={{ padding: '2px 8px', background: '#e0e7ff', color: '#3730a3', borderRadius: '99px', fontSize: '0.7rem', fontWeight: '600' }}>{ev}</span>
                                  ))}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                <button
                                  onClick={async () => {
                                    await fetch(`/api/webhooks/${wh._id}/test`, { method: 'POST', headers });
                                    setStatus(s => ({ ...s, [`wh_test_${wh._id}`]: 'sent' }));
                                    setTimeout(() => setStatus(s => { const n = { ...s }; delete n[`wh_test_${wh._id}`]; return n; }), 3000);
                                  }}
                                  style={{ padding: '0.35rem 0.65rem', background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                                  {status[`wh_test_${wh._id}`] ? '✓ Sent' : 'Test'}
                                </button>
                                <button
                                  onClick={async () => {
                                    await fetch(`/api/webhooks/${wh._id}`, { method: 'PUT', headers, body: JSON.stringify({ active: !wh.active }) });
                                    setWebhooks(prev => prev.map(w => w._id === wh._id ? { ...w, active: !w.active } : w));
                                  }}
                                  style={{ padding: '0.35rem 0.65rem', background: '#f9fafb', color: 'var(--ce-settings-label)', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                                  {wh.active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm('Delete this webhook?')) return;
                                    await fetch(`/api/webhooks/${wh._id}`, { method: 'DELETE', headers });
                                    setWebhooks(prev => prev.filter(w => w._id !== wh._id));
                                  }}
                                  style={{ padding: '0.35rem 0.65rem', background: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </div>
            )}

            {/* ── Payroll Visibility ── */}
            {user?.role === 'superadmin' && (
              <div ref={sectionRefs.payrollVisibility} style={sectionCardStyle}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--ce-settings-text)' }}>Payroll Visibility</h2>
                <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1.5rem' }}>
                  Control which roles can view pay rates outside of their default access. By default, admins and managers only see their own pay.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '1.5rem' }}>
                  <Toggle
                    value={payrollVis.adminsCanSeeManagerPay}
                    onChange={() => setPayrollVis(v => ({ ...v, adminsCanSeeManagerPay: !v.adminsCanSeeManagerPay }))}
                    label="Admins can see manager pay"
                    desc="Admins will be able to view hourly rates and pay totals for manager-role accounts."
                  />
                  <Toggle
                    value={payrollVis.adminsCanSeeOtherAdminPay}
                    onChange={() => setPayrollVis(v => ({ ...v, adminsCanSeeOtherAdminPay: !v.adminsCanSeeOtherAdminPay }))}
                    label="Admins can see other admin pay"
                    desc="Admin accounts will be able to view the pay of other admin accounts in your organization."
                  />
                  <Toggle
                    value={payrollVis.managersCanSeeAdminPay}
                    onChange={() => setPayrollVis(v => ({ ...v, managersCanSeeAdminPay: !v.managersCanSeeAdminPay }))}
                    label="Managers can see admin pay"
                    desc="Manager accounts will be able to view the pay of admin accounts in your organization."
                  />
                </div>

                <div style={{ padding: '0.875rem 1rem', background: 'var(--ce-input-bg, #f9fafb)', border: '1px solid var(--ce-border, #e5e7eb)', borderRadius: '8px', marginBottom: '1.25rem' }}>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--ce-settings-subtext)' }}>
                    <strong>Always enforced:</strong> Owners always see all pay. Admins and managers never see owner pay. Managers only see their own team's employee pay. Co-owner pay visibility is controlled per-owner in the Profile section above.
                  </p>
                </div>

                <button onClick={savePayrollVis} style={saveBtnStyle}>Save Payroll Visibility</button>
                <StatusMsg k="payrollVis" />
              </div>
            )}

            {/* ── Subscription ── */}
            {isSuperAdmin() && (
              <div ref={sectionRefs.subscription} style={sectionCardStyle}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem', color: 'var(--ce-settings-text)' }}>Subscription & Billing</h2>
                <div style={{ padding: '1.25rem', background: planDisplay.bg, borderRadius: '10px', border: `1.5px solid ${planDisplay.border}`, marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: '700', fontSize: '1rem', color: planDisplay.color }}>{planDisplay.name} Plan</p>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.825rem', color: 'var(--ce-settings-subtext)' }}>{planDisplay.desc}</p>
                    </div>
                    <span style={{ padding: '0.25rem 0.75rem', background: planDisplay.color, color: 'white', borderRadius: '99px', fontSize: '0.75rem', fontWeight: '700' }}>Active</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
                  <button style={{ padding: '0.6rem 1.25rem', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>View Billing History</button>
                  <button style={{ padding: '0.6rem 1.25rem', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>Update Payment Method</button>
                  <button onClick={() => setShowChangePlan(true)} style={{ padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #22c55e, #0f4d2e)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>Change Plan</button>
                </div>
                <StatusMsg k="subscription" />
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '1.5rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#ef4444', marginBottom: '0.5rem' }}>Danger Zone</h3>
                  <p style={{ fontSize: '0.825rem', color: 'var(--ce-settings-subtext)', marginBottom: '1rem' }}>These actions are permanent and cannot be undone.</p>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                      onClick={() => { if (window.confirm('Cancel subscription?')) alert('Cancellation requested. You will receive a confirmation email.'); }}
                      style={{ padding: '0.6rem 1.25rem', background: '#fff7ed', color: '#c2410c', border: '1.5px solid #fed7aa', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
                    >
                      Cancel Subscription
                    </button>
                    <button
                      onClick={() => { if (window.confirm('Delete ALL data permanently?')) { if (window.confirm('Last warning. Confirm deletion?')) alert('Account deletion requested.'); } }}
                      style={{ padding: '0.6rem 1.25rem', background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
                    >
                      <><IcoTrash size={15}/> Delete Account</>
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {showChangePlan && (
        <ChangePlanModal
          currentPlan={currentPlan}
          onClose={() => setShowChangePlan(false)}
          onSelectPlan={handlePlanChange}
        />
      )}
    </div>
  );
}

// ── Time Off Policy sub-component ─────────────────────────────────────────────
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const EMP_TYPES = [
  { key: 'fulltime', label: 'Full-time' },
  { key: 'parttime', label: 'Part-time' },
  { key: 'salaried', label: 'Salaried'  },
];

function SettingsPolicySection({ title, policy, onChange, inputStyle, labelStyle }) {
  const isFrontload = policy.calculationType === 'frontload';
  const hours = policy.hours || { fulltime: 0, parttime: 0, salaried: 0 };

  const setHours = (key, val) =>
    onChange({ hours: { ...hours, [key]: parseFloat(val) || 0 } });

  return (
    <div>
      <p style={{ fontWeight: '700', fontSize: '0.95rem', color: 'var(--ce-settings-label)', margin: '0 0 0.875rem' }}>{title}</p>

      {/* Calculation type */}
      <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '1.25rem' }}>
        {[
          { value: 'frontload', label: 'Front-load', desc: 'Full allocation given at the start of each year.' },
          { value: 'accrual',   label: 'Accrual',    desc: 'Hours earned gradually each pay period.' },
        ].map(opt => (
          <label key={opt.value} style={{ flex: 1, display: 'flex', gap: '0.65rem', alignItems: 'flex-start', padding: '0.875rem', background: policy.calculationType === opt.value ? '#f0f4ff' : '#f9fafb', border: `2px solid ${policy.calculationType === opt.value ? '#22c55e' : '#e5e7eb'}`, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s' }}>
            <input type="radio" value={opt.value} checked={policy.calculationType === opt.value} onChange={() => onChange({ calculationType: opt.value })} style={{ marginTop: '0.2rem', accentColor: '#22c55e' }} />
            <div>
              <div style={{ fontWeight: '700', fontSize: '0.875rem', color: 'var(--ce-settings-text)' }}>{opt.label}</div>
              <div style={{ fontSize: '0.775rem', color: 'var(--ce-settings-subtext)', marginTop: '0.2rem' }}>{opt.desc}</div>
            </div>
          </label>
        ))}
      </div>

      {/* Hours per employment type */}
      <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem', border: '1px solid #e5e7eb' }}>
        <p style={{ margin: '0 0 0.875rem', fontSize: '0.82rem', fontWeight: '700', color: 'var(--ce-settings-label)' }}>
          {isFrontload ? 'Annual hours by employment type' : 'Hours earned per period by employment type'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          {EMP_TYPES.map(({ key, label }) => (
            <div key={key}>
              <label style={{ ...labelStyle, fontSize: '0.8rem' }}>{label}</label>
              <input
                type="number" min="0" step="0.5"
                value={hours[key] ?? 0}
                onChange={e => setHours(key, e.target.value)}
                style={inputStyle}
                placeholder="0"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Period / reset month + carryover */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {isFrontload ? (
          <div>
            <label htmlFor="setting-reset-month" style={labelStyle}>Reset month</label>
            <select id="setting-reset-month" value={policy.frontloadResetMonth ?? 1} onChange={e => onChange({ frontloadResetMonth: parseInt(e.target.value) })} style={inputStyle}>
              {MONTHS_SHORT.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label htmlFor="setting-accrual-period" style={labelStyle}>Accrual period</label>
            <select id="setting-accrual-period" value={policy.accrualPeriod ?? 'biweekly'} onChange={e => onChange({ accrualPeriod: e.target.value })} style={inputStyle}>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        )}
        <div>
          <label style={labelStyle}>Carryover limit (hrs)</label>
          <input type="number" min="-1" step="0.5" value={policy.carryoverLimit ?? 0} onChange={e => onChange({ carryoverLimit: parseFloat(e.target.value) ?? 0 })} style={inputStyle} />
          <p style={{ fontSize: '0.72rem', color: 'var(--ce-settings-subtext)', margin: '0.3rem 0 0' }}>0 = none &nbsp;·&nbsp; -1 = unlimited</p>
        </div>
      </div>
    </div>
  );
}